import { formatCurrency } from '../../utility';
import type { JSX } from 'preact';

const NON_NUMERIC_CHARS = /[^0-9.]/g;

export type DataAttributes = {
  allowNegative?: boolean;
  disableCents?: boolean;
  output?: 'cents' | 'dollars';
};

export const Events = {
  input: handleInput,
  blur: handleBlur,
};

export function getDataAttributes({
  allowNegative = false,
  disableCents = false,
  output = 'cents',
}: DataAttributes) {
  return {
    'data-allow-negative': allowNegative,
    'data-disable-cents': disableCents,
    'data-output': output,
    placeholder: '$',
  };
}

function handleBlur(e: JSX.TargetedEvent<HTMLInputElement>) {
  const { currentTarget } = e;
  const { nextElementSibling, value } = currentTarget;
  const hiddenField = nextElementSibling as HTMLInputElement;
  const [dollars, cents = ''] = value.split('.');

  if (value === '-' || value === '-$') {
    currentTarget.value = '';
    hiddenField.value = '';
  } else if (cents.length === 1) {
    // add trailing 0 (e.g. user blurs on $123.4)
    currentTarget.value += '0';
  } else if (!cents.length) {
    // remove trailing decimal point (e.g. user blurs on $123.)
    currentTarget.value = dollars;
    hiddenField.value = hiddenField.value.replace(/\./, '');
  }
}

function handleInput(e: JSX.TargetedEvent<HTMLInputElement>) {
  const { currentTarget } = e;
  const { nextElementSibling, value } = currentTarget;
  const hiddenField = nextElementSibling as HTMLInputElement;
  const { allowNegative, disableCents, output } = currentTarget.dataset;
  const canAllowCents = disableCents !== 'true';
  const canAllowNegative = allowNegative === 'true';
  // @ts-ignore event can be a custom event with detail object
  const isInitialRender = !!e.detail?.isInitialRender;
  const isNegative = canAllowNegative && value.startsWith('-');
  let numValue = value.replace(NON_NUMERIC_CHARS, '') || '';
  let rawNumber = Number(numValue);

  // on intial mount/render ensure the output is correct
  if (isInitialRender && output === 'cents') {
    if (numValue) {
      rawNumber = rawNumber / 100;
      numValue = rawNumber.toString();
    }
  }

  const [dollars = '', cents = ''] = numValue.split('.');

  let normalizedCents = canAllowCents ? cents : '00';

  if (normalizedCents.length === 1) {
    normalizedCents = `${normalizedCents}0`;
  } else if (cents.length > 2) {
    normalizedCents = normalizedCents.substring(0, 2);
  }

  if (!numValue) {
    currentTarget.value = isNegative ? '-' : '';
    hiddenField.value = '';
  } else {
    const hasDecimal = numValue.includes('.');
    const formattedDollars = formatCurrency(dollars, 'ToNearestDollar');
    const centsForInput =
      hasDecimal && canAllowCents ? `.${cents.length < 2 ? cents : normalizedCents}` : '';
    let numericVersion = `${dollars}${centsForInput}`;

    if (numericVersion === '.') {
      numericVersion = '0';
    }

    currentTarget.value = `${formattedDollars}${centsForInput}`;

    if (output === 'cents') {
      hiddenField.value = (Number(numericVersion) * 100).toFixed(0); // need to round; e.g. 34.37 * 100 === 3436.9999999999995
    } else {
      hiddenField.value = numericVersion;
    }

    if (isNegative) {
      currentTarget.value = `-${currentTarget.value}`;
      hiddenField.value = `-${hiddenField.value}`;
    }

    // ensure formatting is applied
    if (isInitialRender) {
      currentTarget.dispatchEvent(new CustomEvent('blur'));
    }
  }
}
